import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Search, Plus, Edit2, Trash2, X, Filter, FileText, Image as ImageIcon, UploadCloud, Loader2 } from "lucide-react";
import { hardwareApi } from "../../../services/labApi";
import Wrapper from "../../../components/Wrapper";
import { uploadImageToS3, deleteImageFromS3, getFileExtension } from "../../../utils/s3Upload"; 

export default function ElectronicComponentsPage() {
  const [components, setComponents] = useState([]);
  const [search, setSearch] = useState("");
  const [hardwareList, setHardwareList] = useState([]);
  const [selectedHardwareId, setSelectedHardwareId] = useState("");
  
  // Modals state
  const [showComponentModal, setShowComponentModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showViewBatchesModal, setShowViewBatchesModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  
  // Selected Data state
  const [editingComponent, setEditingComponent] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [componentBatches, setComponentBatches] = useState([]);
  const [updatingBatchId, setUpdatingBatchId] = useState(null);
  
  // Forms & Upload state
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [isInvoiceUploading, setIsInvoiceUploading] = useState(false);
  const invoiceInputRef = useRef(null);
  
  const [componentForm, setComponentForm] = useState({ 
    name: "", 
    unit: "pcs", 
    current_stock: "", 
    min_quantity: "",
    vendors: [{ name: "", mobile: "", email: "" }],
    lead_time_days: "",
    total_cost: "",
    image_url: ""
  });
  
  const [batchForm, setBatchForm] = useState({
    batch_code: "",
    quantity: "",
    received_date: new Date().toISOString().split("T")[0],
    unit_cost: "",
    total_cost: "",
    vendor_name: "",
    invoice_url: ""
  });

  const [notesText, setNotesText] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (selectedHardwareId) {
      params.hardware_id = selectedHardwareId;
    }

    const [compRows, hwRows] = await Promise.all([
      hardwareApi.getComponents(params),
      hardwareApi.getHardware()
    ]);
    
    setComponents(compRows);
    setHardwareList(hwRows);
  }, [selectedHardwareId]);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  const filteredComponents = useMemo(() => {
    const filtered = components.filter((c) => 
      c.name.toLowerCase().includes(search.toLowerCase())
    );

    return filtered.sort((a, b) => {
      const aLowStock = Number(a.current_stock) <= Number(a.min_quantity || 0);
      const bLowStock = Number(b.current_stock) <= Number(b.min_quantity || 0);

      if (aLowStock && !bLowStock) return -1;
      if (!aLowStock && bLowStock) return 1;
      
      return a.name.localeCompare(b.name);
    });
  }, [components, search]);

  const openComponentModal = (component) => {
    setEditingComponent(component || null);
    setComponentForm(
      component
        ? {
            name: component.name || "",
            unit: component.unit || "pcs",
            current_stock: String(component.current_stock || ""),
            min_quantity: String(component.min_quantity || ""),
            vendors: component.vendors?.length ? component.vendors : [{ name: "", mobile: "", email: "" }],
            lead_time_days: String(component.lead_time_days || ""),
            total_cost: String(component.total_cost || ""),
            image_url: component.image_url || ""
          }
        : { name: "", unit: "pcs", current_stock: "", min_quantity: "", vendors: [{ name: "", mobile: "", email: "" }], lead_time_days: "", total_cost: "", image_url: "" }
    );
    setShowComponentModal(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    try {
      const ext = getFileExtension(file);
      if (componentForm.image_url) await deleteImageFromS3(componentForm.image_url);
      
      const url = await uploadImageToS3(file, ext, 'hardware-components');
      setComponentForm(prev => ({ ...prev, image_url: url }));
    } catch (err) {
      alert("Failed to upload image. Please try again.");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveImage = async () => {
    if (!componentForm.image_url) return;
    try {
      await deleteImageFromS3(componentForm.image_url);
      setComponentForm(prev => ({ ...prev, image_url: "" }));
    } catch (err) {
      console.error(err);
      setComponentForm(prev => ({ ...prev, image_url: "" }));
    }
  };

  const handleComponentSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: componentForm.name,
      unit: componentForm.unit,
      current_stock: Number(componentForm.current_stock || 0),
      min_quantity: Number(componentForm.min_quantity || 0),
      vendors: componentForm.vendors,
      lead_time_days: Number(componentForm.lead_time_days || 0),
      total_cost: Number(componentForm.total_cost || 0),
      notes: editingComponent?.notes || "",
      image_url: componentForm.image_url
    };
    
    if (editingComponent) {
      await hardwareApi.updateComponent(editingComponent.id, payload);
    } else {
      await hardwareApi.createComponent(payload);
    }
    
    setShowComponentModal(false);
    await load();
  };

  const openNotesModal = (component) => {
    setSelectedComponent(component);
    setNotesText(component.notes || "");
    setShowNotesModal(true);
  };

  const handleNotesSubmit = async (e) => {
    e.preventDefault();
    if (!selectedComponent) return;

    const payload = {
      name: selectedComponent.name,
      unit: selectedComponent.unit,
      current_stock: Number(selectedComponent.current_stock || 0),
      min_quantity: Number(selectedComponent.min_quantity || 0),
      vendors: selectedComponent.vendors || [],
      lead_time_days: Number(selectedComponent.lead_time_days || 0),
      total_cost: Number(selectedComponent.total_cost || 0),
      image_url: selectedComponent.image_url,
      notes: notesText
    };

    await hardwareApi.updateComponent(selectedComponent.id, payload);
    setShowNotesModal(false);
    await load();
  };

  const handleDeleteComponent = async (id) => {
    if (window.confirm("Are you sure you want to delete this component? This action cannot be undone.")) {
      try {
        await hardwareApi.deleteComponent(id);
        await load();
      } catch (error) {
        console.error("Failed to delete component:", error);
        alert("Failed to delete component. It might have associated batches.");
      }
    }
  };

  const openBatchModal = (component) => {
    setSelectedComponent(component);
    const defaultVendor = component.vendors && component.vendors.length > 0 ? component.vendors[0].name : "";
    
    setBatchForm({
      batch_code: `${component.name.slice(0, 3).toUpperCase()}-${Date.now().toString().slice(-6)}`,
      quantity: "",
      received_date: new Date().toISOString().split("T")[0],
      unit_cost: "",
      total_cost: "",
      vendor_name: defaultVendor,
      invoice_url: ""
    });
    setShowBatchModal(true);
  };

  const handleBatchSubmit = async (e) => {
    e.preventDefault();
    if (!selectedComponent) return;
    
    await hardwareApi.addBatch(selectedComponent.id, {
      batch_code: batchForm.batch_code,
      quantity: Number(batchForm.quantity || 0),
      received_date: batchForm.received_date,
      total_cost: Number(batchForm.total_cost || 0),
      vendor_name: batchForm.vendor_name,
      invoice_url: batchForm.invoice_url
    });
    
    setShowBatchModal(false);
    await load();
  };

  const openViewBatchesModal = async (component) => {
    setSelectedComponent(component);
    setShowViewBatchesModal(true);
    const batches = await hardwareApi.getBatches(component.id);
    setComponentBatches(batches);
  };

  const updateBatchStatus = async (batchId, status) => {
    try {
      setUpdatingBatchId(batchId);
      await hardwareApi.updateBatchStatus(batchId, status);
      
      await load();
  
      if (selectedComponent) {
        const updatedBatches = await hardwareApi.getBatches(selectedComponent.id);
        setComponentBatches(updatedBatches);
      }
    } finally {
      setUpdatingBatchId(null);
    }
  };

  const handleVendorChange = (index, field, value) => {
    const newVendors = [...componentForm.vendors];
    newVendors[index][field] = value;
    setComponentForm({ ...componentForm, vendors: newVendors });
  };

  const addVendor = () => {
    setComponentForm({ 
      ...componentForm, 
      vendors: [...componentForm.vendors, { name: "", mobile: "", email: "" }] 
    });
  };

  const removeVendor = (index) => {
    const newVendors = componentForm.vendors.filter((_, i) => i !== index);
    setComponentForm({ ...componentForm, vendors: newVendors });
  };

  const VendorCell = ({ vendors }) => {
    const [expanded, setExpanded] = useState(false);

    if (!vendors || vendors.length === 0) return <span>-</span>;

    const firstVendor = vendors[0];
    const hasMore = vendors.length > 1;

    return (
      <div className="space-y-1.5 min-w-[140px]">
        <div className="leading-tight">
          <span className="font-medium text-slate-700">{firstVendor.name || "-"}</span>
          {(firstVendor.mobile || firstVendor.email) && (
            <span className="block text-[11px] text-slate-400">
              {firstVendor.mobile} {firstVendor.mobile && firstVendor.email && "•"} {firstVendor.email}
            </span>
          )}
        </div>

        {expanded && (
          <div className="space-y-2 pt-2 mt-2 border-t border-slate-100">
            {vendors.slice(1).map((v, i) => (
              <div key={i} className="leading-tight">
                <span className="font-medium text-slate-700">{v.name || "-"}</span>
                {(v.mobile || v.email) && (
                  <span className="block text-[11px] text-slate-400">
                    {v.mobile} {v.mobile && v.email && "•"} {v.email}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {hasMore && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[11px] text-emerald-600 hover:text-emerald-800 font-medium inline-block mt-1"
          >
            {expanded ? "Show Less" : `+ ${vendors.length - 1} more`}
          </button>
        )}
      </div>
    );
  };

  const handleInvoiceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsInvoiceUploading(true);
    try {
      const ext = getFileExtension(file);
      if (batchForm.invoice_url) await deleteImageFromS3(batchForm.invoice_url);
      
      const url = await uploadImageToS3(file, ext, 'hardware-invoices');
      setBatchForm(prev => ({ ...prev, invoice_url: url }));
    } catch (err) {
      alert("Failed to upload invoice. Please try again.");
    } finally {
      setIsInvoiceUploading(false);
      if (invoiceInputRef.current) invoiceInputRef.current.value = "";
    }
  };

  const handleRemoveInvoice = async () => {
    if (!batchForm.invoice_url) return;
    try {
      await deleteImageFromS3(batchForm.invoice_url);
      setBatchForm(prev => ({ ...prev, invoice_url: "" }));
    } catch (err) {
      console.error(err);
      setBatchForm(prev => ({ ...prev, invoice_url: "" }));
    }
  };

  return (
    <Wrapper>
      <div className="space-y-6 pb-20 lg:pb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="w-full text-left">
            <h2 className="m-0 text-2xl font-bold text-slate-800 text-left">Electronic Components</h2>
            <p className="mt-1 text-slate-500 text-left">Hardware inventory and batch tracking</p>
          </div>
          <button onClick={() => openComponentModal(null)} className="flex items-center gap-2 self-start bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700">
            <Plus size={18} />
            Add Component
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input 
              value={search} 
              onChange={(e) => setSearch(e.target.value)} 
              placeholder="Search components..." 
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg" 
            />
          </div>
          
          <div className="relative min-w-[250px]">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
              <Filter size={16} />
            </div>
            <select
              value={selectedHardwareId}
              onChange={(e) => setSelectedHardwareId(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg appearance-none bg-white text-slate-700"
            >
              <option value="">All Components</option>
              {hardwareList.map(hw => (
                <option key={hw.id} value={hw.id}>
                  Used in: {hw.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-3 text-left text-sm w-10">S.No</th>
                <th className="px-3 py-3 text-left text-sm w-14">Image</th>
                <th className="px-3 py-3 text-left text-sm min-w-[120px]">Name</th>
                <th className="px-3 py-3 text-left text-sm w-1/3 min-w-[200px]">Notes</th>
                <th className="px-3 py-3 text-left text-sm">Vendor</th>
                <th className="px-3 py-3 text-left text-sm w-12">Unit</th>
                <th className="px-3 py-3 text-right text-sm whitespace-nowrap">Stock</th>
                <th className="px-3 py-3 text-right text-sm whitespace-nowrap">Min Alert</th>
                <th className="px-3 py-3 text-right text-sm whitespace-nowrap">Total Value</th>
                <th className="px-3 py-3 text-right text-sm">Batches</th>
                <th className="px-3 py-3 text-right text-sm">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredComponents.map((c, index) => (
                <tr key={c.id}>
                  <td className="px-3 py-3 text-slate-500">{index + 1}</td>
                  <td className="px-3 py-3">
                    {c.image_url ? (
                      <a href={c.image_url} target="_blank" rel="noreferrer">
                        <img src={c.image_url} alt={c.name} className="w-10 h-10 object-cover rounded shadow-sm border border-slate-200" />
                      </a>
                    ) : (
                      <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center text-slate-300">
                        <ImageIcon size={18} />
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 font-medium text-slate-800">
                    {c.name}
                  </td>
                  <td className="px-3 py-3">
                    <div className="group flex items-start gap-2 w-full">
                      <div 
                        className="text-xs text-slate-600 cursor-pointer flex-1 whitespace-pre-wrap break-words leading-relaxed" 
                        title={c.notes ? "Click to edit notes" : "Click to add notes"}
                        onClick={() => openNotesModal(c)}
                      >
                        {c.notes ? c.notes : <span className="text-slate-400 italic">Add note...</span>}
                      </div>
                      <button 
                        onClick={() => openNotesModal(c)}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-emerald-600 transition-all shrink-0 -mt-1"
                        title="Edit Notes"
                      >
                        <Edit2 size={14} />
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-slate-600">
                    <VendorCell vendors={c.vendors} />
                  </td>
                  <td className="px-3 py-3">{c.unit}</td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${Number(c.current_stock) <= Number(c.min_quantity || 0) ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}`}>
                    {c.current_stock}
                  </span>
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">{c.min_quantity}</td>
                  <td className="px-3 py-3 text-right font-medium text-emerald-700 whitespace-nowrap">
                    ₹{Number(c.total_cost || 0).toFixed(2)}
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex gap-2">
                      <button onClick={() => openViewBatchesModal(c)} className="px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-sm">
                        View Batches
                      </button>
                      <button onClick={() => openBatchModal(c)} className="px-2 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-sm">
                        Add Batch
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex gap-1">
                        <button onClick={() => openComponentModal(c)} className="p-2 hover:bg-slate-100 rounded-lg" title="Edit Component">
                        <Edit2 size={16} />
                        </button>
                        <button 
                        onClick={() => handleDeleteComponent(c.id)} 
                        className="p-2 hover:bg-red-50 rounded-lg text-red-500"
                        title="Delete Component"
                        >
                        <Trash2 size={16} />
                        </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* --- NOTES MODAL --- */}
        {showNotesModal && selectedComponent && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-md">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <FileText size={18} className="text-emerald-600" />
                  Notes: {selectedComponent.name}
                </h3>
                <button onClick={() => setShowNotesModal(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X size={20} /></button>
              </div>
              <form onSubmit={handleNotesSubmit} className="p-4 space-y-3">
                <div>
                  <textarea 
                    value={notesText} 
                    onChange={(e) => setNotesText(e.target.value)} 
                    placeholder="Add technical details, alternatives, or other notes here..." 
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-500 min-h-[150px] resize-y" 
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => setShowNotesModal(false)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg">Save Notes</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* --- ADD/EDIT COMPONENT MODAL --- */}
        {showComponentModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
                <h3 className="text-lg font-semibold">{editingComponent ? "Edit Component" : "Add Component"}</h3>
                <button onClick={() => setShowComponentModal(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X size={20} /></button>
              </div>
              <form onSubmit={handleComponentSubmit} className="p-4 space-y-4">
                
                {/* Image Upload Section */}
                <div className="flex gap-4 items-start border border-slate-200 rounded-xl p-4 bg-slate-50">
                  <div className="w-24 h-24 shrink-0 bg-white border border-slate-200 rounded-lg overflow-hidden flex items-center justify-center relative group">
                    {isUploading ? (
                      <Loader2 size={24} className="text-emerald-600 animate-spin" />
                    ) : componentForm.image_url ? (
                      <>
                        <img src={componentForm.image_url} alt="Component preview" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <button type="button" onClick={handleRemoveImage} className="p-1.5 bg-red-500 text-white rounded hover:bg-red-600">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </>
                    ) : (
                      <ImageIcon size={32} className="text-slate-300" />
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Component Image</label>
                    <p className="text-xs text-slate-500 mb-3">Upload a reference photo (JPG, PNG). Max 2MB recommended.</p>
                    <input 
                      type="file" 
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload"
                      disabled={isUploading}
                    />
                    <label htmlFor="image-upload" className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium cursor-pointer transition-colors ${isUploading ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'}`}>
                      <UploadCloud size={16} />
                      {isUploading ? 'Uploading...' : 'Choose Image'}
                    </label>
                  </div>
                </div>

                <input value={componentForm.name} onChange={(e) => setComponentForm({ ...componentForm, name: e.target.value })} placeholder="Component Name" className="w-full px-3 py-2 border rounded-lg" required />
                
                <div className="grid grid-cols-2 gap-3">
                  <input value={componentForm.unit} onChange={(e) => setComponentForm({ ...componentForm, unit: e.target.value })} placeholder="Unit (e.g. pcs)" className="w-full px-3 py-2 border rounded-lg" required />
                  <input value={componentForm.min_quantity} onChange={(e) => setComponentForm({ ...componentForm, min_quantity: e.target.value })} type="number" placeholder="Min Stock Alert" className="w-full px-3 py-2 border rounded-lg" />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Current Stock</label>
                    <input value={componentForm.current_stock} onChange={(e) => setComponentForm({ ...componentForm, current_stock: e.target.value })} type="number" placeholder="0" className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Total Value (₹)</label>
                    <input value={componentForm.total_cost} onChange={(e) => setComponentForm({ ...componentForm, total_cost: e.target.value })} type="number" step="0.01" placeholder="0.00" className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                </div>
                
                <div className="space-y-3 border border-slate-200 rounded-xl p-4 bg-slate-50">
                  <div className="flex justify-between items-center">
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Vendors
                    </label>
                    <button 
                      type="button" 
                      onClick={addVendor}
                      className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1 bg-emerald-50 px-2 py-1 rounded-lg"
                    >
                      <Plus size={16} /> Add Vendor
                    </button>
                  </div>
                  
                  <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                    {componentForm.vendors.map((vendor, index) => (
                      <div key={index} className="p-3 bg-white border border-slate-200 rounded-lg relative space-y-3 shadow-sm">
                        
                        <div className="flex gap-2 items-center">
                          <input 
                            value={vendor.name} 
                            onChange={(e) => handleVendorChange(index, 'name', e.target.value)} 
                            placeholder="Vendor Name" 
                            className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-500" 
                            required 
                          />
                          <button 
                            type="button" 
                            onClick={() => removeVendor(index)}
                            disabled={componentForm.vendors.length === 1}
                            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-30 rounded-lg transition-colors"
                            title="Remove Vendor"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <input 
                            value={vendor.mobile} 
                            onChange={(e) => handleVendorChange(index, 'mobile', e.target.value)} 
                            placeholder="Mobile / Landline" 
                            maxLength={11}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-500" 
                          />
                          <input 
                            value={vendor.email} 
                            onChange={(e) => handleVendorChange(index, 'email', e.target.value)} 
                            placeholder="Email Address" 
                            type="email"
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-500" 
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <input value={componentForm.lead_time_days} onChange={(e) => setComponentForm({ ...componentForm, lead_time_days: e.target.value })} type="number" placeholder="Lead Time (Days)" className="w-full px-3 py-2 border rounded-lg" />

                <div className="flex gap-2 pt-2 sticky bottom-0 bg-white">
                  <button type="button" onClick={() => setShowComponentModal(false)} className="flex-1 px-4 py-2 border rounded-lg bg-white">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* --- ADD BATCH MODAL --- */}
        {showBatchModal && selectedComponent && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-md">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold">Add Batch: {selectedComponent.name}</h3>
                <button onClick={() => setShowBatchModal(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X size={20} /></button>
              </div>
              <form onSubmit={handleBatchSubmit} className="p-4 space-y-3">
                <input 
                  value={batchForm.batch_code} 
                  onChange={(e) => setBatchForm({ ...batchForm, batch_code: e.target.value })} 
                  placeholder="Batch Code" 
                  className="w-full px-3 py-2 border rounded-lg" 
                  required 
                />
                
                {/* VENDOR DROPDOWN */}
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Received From (Vendor)</label>
                  <select 
                    value={batchForm.vendor_name}
                    onChange={(e) => setBatchForm({ ...batchForm, vendor_name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:border-emerald-500"
                  >
                    <option value="">-- Select Vendor --</option>
                    {(selectedComponent.vendors || []).map((v, idx) => {
                      if (!v.name) return null; // Skip empty vendor blocks
                      return (
                        <option key={idx} value={v.name}>{v.name}</option>
                      )
                    })}
                  </select>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {/* Quantity and Date inputs */}
                  <input 
                    value={batchForm.quantity} 
                    onChange={(e) => {
                      const qty = e.target.value;
                      setBatchForm(prev => ({
                        ...prev, 
                        quantity: qty,
                        total_cost: prev.unit_cost && qty ? (Number(prev.unit_cost) * Number(qty)).toFixed(2) : prev.total_cost
                      }));
                    }} 
                    type="number" 
                    placeholder="Quantity" 
                    className="w-full px-3 py-2 border rounded-lg" 
                    required 
                  />
                  <input 
                    value={batchForm.received_date} 
                    onChange={(e) => setBatchForm({ ...batchForm, received_date: e.target.value })} 
                    type="date" 
                    className="w-full px-3 py-2 border rounded-lg" 
                    required 
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {/* Cost inputs */}
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Unit Cost (Optional)</label>
                    <input 
                      value={batchForm.unit_cost} 
                      onChange={(e) => {
                        const uc = e.target.value;
                        setBatchForm(prev => ({
                          ...prev, 
                          unit_cost: uc,
                          total_cost: prev.quantity && uc ? (Number(prev.quantity) * Number(uc)).toFixed(2) : prev.total_cost
                        }));
                      }} 
                      type="number" 
                      step="0.01"
                      placeholder="e.g. 2.50" 
                      className="w-full px-3 py-2 border rounded-lg" 
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Total Batch Cost</label>
                    <input 
                      value={batchForm.total_cost} 
                      onChange={(e) => {
                        const tc = e.target.value;
                        setBatchForm(prev => ({
                          ...prev, 
                          total_cost: tc,
                          unit_cost: prev.quantity && tc ? (Number(tc) / Number(prev.quantity)).toFixed(4) : prev.unit_cost
                        }));
                      }} 
                      type="number" 
                      step="0.01"
                      placeholder="e.g. 500.00" 
                      className="w-full px-3 py-2 border rounded-lg" 
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-slate-500 mb-1">Invoice / Receipt (Optional)</label>
                  {batchForm.invoice_url ? (
                    <div className="flex items-center justify-between p-2 border border-slate-200 bg-slate-50 rounded-lg">
                      <a href={batchForm.invoice_url} target="_blank" rel="noreferrer" className="text-sm text-emerald-600 hover:underline flex items-center gap-2">
                        <FileText size={16} /> View Attachment
                      </a>
                      <button type="button" onClick={handleRemoveInvoice} className="text-red-500 hover:bg-red-50 p-1.5 rounded-md transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="w-full">
                      <input 
                        type="file" 
                        accept="image/*,application/pdf"
                        ref={invoiceInputRef}
                        onChange={handleInvoiceUpload}
                        className="hidden"
                        id="invoice-upload"
                        disabled={isInvoiceUploading}
                      />
                      <label htmlFor="invoice-upload" className={`flex items-center justify-center gap-2 w-full px-3 py-2 border border-dashed rounded-lg text-sm font-medium cursor-pointer transition-colors ${isInvoiceUploading ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                        {isInvoiceUploading ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
                        {isInvoiceUploading ? 'Uploading...' : 'Upload PDF or Capture Photo'}
                      </label>
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => setShowBatchModal(false)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg">Submit</button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* --- VIEW BATCHES MODAL --- */}
        {showViewBatchesModal && selectedComponent && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-2xl">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold">
                  Batches: {selectedComponent.name}
                </h3>
                <button onClick={() => setShowViewBatchesModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X size={20} />
                </button>
              </div>
              <div className="p-4 max-h-[60vh] overflow-y-auto">
                {componentBatches.length === 0 ? (
                  <p className="text-sm text-slate-500">No batches found for this component.</p>
                ) : (
                  <div className="space-y-2">
                    {componentBatches.map((batch) => {
                      const status = String(batch.status || "approved").toLowerCase();
                      
                      const receivedDate = new Date(batch.received_date);
                      const now = new Date();
                      const diffDays = (now - receivedDate) / (1000 * 60 * 60 * 24);
                      const isWithin7Days = diffDays <= 7;
                      
                      return (
                        <div key={batch.id} className="border border-slate-200 rounded-lg p-3">
                          <div className="flex justify-between items-start">
                              <div>
                                <p className="m-0 font-medium text-slate-800">{batch.batch_code || `Batch #${batch.id}`}</p>
                                <p className="m-0 text-sm text-slate-600">
                                  Qty: {batch.quantity} | Status: <span className="capitalize">{status}</span> 
                                  {batch.total_cost > 0 && ` | Cost: ₹${Number(batch.total_cost).toFixed(2)}`}
                                </p>
                                <p className="m-0 text-xs text-slate-500 mt-1">
                                  Received: {batch.received_date ? String(batch.received_date).split("T")[0] : "-"} 
                                  {batch.vendor_name ? ` • Vendor: ${batch.vendor_name}` : ""}
                                </p>

                                {batch.invoice_url && (
                                  <p className="m-0 mt-1">
                                    <a href={batch.invoice_url} target="_blank" rel="noreferrer" className="text-[11px] text-emerald-600 hover:underline flex items-center gap-1 w-fit bg-emerald-50 px-2 py-0.5 rounded">
                                      <FileText size={12} /> View Invoice
                                    </a>
                                  </p>
                                )}
                                
                                {/* Use updated_at for the return date */}
                                {status === "returned" && batch.updated_at && (
                                  <p className="m-0 text-xs text-red-500 mt-0.5">
                                    Returned on: {String(batch.updated_at).split("T")[0]}
                                  </p>
                                )}
                              </div>
                          </div>
                          
                          <div className="mt-3 flex gap-2">
                            {status === "returned" ? (
                              <span className="px-3 py-1 bg-slate-100 text-slate-500 text-xs rounded-lg font-medium">
                                Returned
                              </span>
                            ) : isWithin7Days ? (
                              <button
                                type="button"
                                disabled={updatingBatchId === batch.id}
                                onClick={() => updateBatchStatus(batch.id, "returned")}
                                className="px-3 py-1 rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 text-xs font-medium transition-colors disabled:opacity-50"
                              >
                                Return Batch
                              </button>
                            ) : (
                              <span className="text-xs text-slate-400 italic">Return window expired</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="p-4 border-t">
                <button type="button" onClick={() => setShowViewBatchesModal(false)} className="px-4 py-2 border rounded-lg">
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
}